export type CartoCiudadCandidate = {
  address: string;
  placeId: string;
  source: "cartociudad";
};
export type CartoCiudadAddressDetails = {
  street?: string;
  streetNumber?: string;
  complement?: string;
  postalCode?: string;
  municipality?: string;
  province?: string;
  autonomousCommunity?: string;
  countryCode?: string;
  reference: string;
  precision?: string;
  provenance: "cartociudad";
};
export type ResolvedCartoCiudadCandidate = CartoCiudadCandidate & {
  latitude: number;
  longitude: number;
  addressDetails?: CartoCiudadAddressDetails;
};

const CARTOCIUDAD_API = "https://www.cartociudad.es/geocoder/api/geocoder";
const SEARCH_TIMEOUT_MS = 8_000;

/** Search is only a candidate list: CartoCiudad callejero candidates may carry placeholder 0,0 coordinates. */
export async function searchCartoCiudad(address: string, fetcher: typeof fetch = fetch): Promise<CartoCiudadCandidate[]> {
  const query = address.trim();
  if (query.length < 5 || query.length > 300) throw new Error("CARTOCIUDAD_QUERY_INVALID");
  const params = new URLSearchParams({ q: query, limit: "5" });
  const response = await fetcher(`${CARTOCIUDAD_API}/candidates?${params}`, {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS),
  });
  if (!response.ok) throw new Error("CARTOCIUDAD_UNAVAILABLE");
  const raw: unknown = await response.json();
  if (!Array.isArray(raw)) throw new Error("CARTOCIUDAD_INVALID_RESPONSE");
  return raw.slice(0, 5).flatMap((entry: any) => {
    if (!entry || typeof entry.address !== "string" || !entry.address.trim() ||
      typeof entry.id !== "string" || !entry.id) return [];
    return [{ address: entry.address.trim(), placeId: entry.id, source: "cartociudad" as const }];
  });
}

function spanishCoordinate(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/** Resolve the selected candidate using the official `find?q=` call documented for callejero results. */
export async function resolveCartoCiudadCandidate(candidate: CartoCiudadCandidate, fetcher: typeof fetch = fetch): Promise<ResolvedCartoCiudadCandidate> {
  if (!candidate?.placeId || !candidate.address?.trim()) throw new Error("CARTOCIUDAD_CANDIDATE_INVALID");
  const params = new URLSearchParams({ q: candidate.address });
  const response = await fetcher(`${CARTOCIUDAD_API}/find?${params}`, {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS),
  });
  if (!response.ok) throw new Error("CARTOCIUDAD_RESOLUTION_UNAVAILABLE");
  const raw: unknown = await response.json();
  const resolved = Array.isArray(raw) ? raw.find((entry: any) => entry?.id === candidate.placeId) : raw;
  if (!resolved || typeof resolved !== "object" || resolved.id !== candidate.placeId) throw new Error("CARTOCIUDAD_RESOLUTION_MISMATCH");
  const latitude = spanishCoordinate((resolved as any).lat);
  const longitude = spanishCoordinate((resolved as any).lng);
  // CartoCiudad's Spanish address service should resolve within Spain, including the Canary Islands, Ceuta and Melilla.
  if (latitude == null || longitude == null || latitude < 27 || latitude > 44.5 || longitude < -19 || longitude > 5.5)
    throw new Error("CARTOCIUDAD_COORDINATES_INVALID");
  const text = (value: unknown) => typeof value === "string" && value.trim() ? value.trim() : undefined;
  const isoCountryCode = text((resolved as any).countryCode);
  const streetType = text((resolved as any).tip_via);
  const streetName = text((resolved as any).address);
  const streetNumber = (resolved as any).portalNumber;
  const details: CartoCiudadAddressDetails = {
    ...(streetType && streetName ? { street: `${streetType} ${streetName}` } : streetName ? { street: streetName } : streetType ? { street: streetType } : {}),
    ...(streetNumber != null ? { streetNumber: String(streetNumber) } : {}),
    ...(text((resolved as any).extension) ? { complement: text((resolved as any).extension) } : {}),
    ...(text((resolved as any).postalCode) ? { postalCode: text((resolved as any).postalCode) } : {}),
    ...(text((resolved as any).poblacion) || text((resolved as any).muni) ? { municipality: text((resolved as any).poblacion) ?? text((resolved as any).muni) } : {}),
    ...(text((resolved as any).province) ? { province: text((resolved as any).province) } : {}),
    ...(text((resolved as any).comunidadAutonoma) ? { autonomousCommunity: text((resolved as any).comunidadAutonoma) } : {}),
    ...(isoCountryCode && /^[A-Za-z]{2}$/.test(isoCountryCode) ? { countryCode: isoCountryCode.toUpperCase() } : {}),
    reference: candidate.placeId,
    ...(text((resolved as any).type) ? { precision: text((resolved as any).type) } : {}),
    provenance: "cartociudad",
  };
  return { ...candidate, latitude, longitude, addressDetails: details };
}
